import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

/**
 * Text-style button with the same look as LinkButton from
 * @communityshare/ui-shared, plus support for aria-label and other button
 * attributes (LinkButton does not forward them). Needed for row actions whose
 * accessible name must include the video title, e.g. "Move Take a tour up".
 * aria-disabled is styled like disabled but keeps the button focusable; the
 * caller is responsible for ignoring clicks in that state.
 */
const TextButton = forwardRef(function TextButton(props, ref) {
  const {
    label,
    icon: Icon,
    tone = 'blue',
    disabled = false,
    className,
    ...rest
  } = props;
  const ariaDisabled = rest['aria-disabled'] === true || rest['aria-disabled'] === 'true';
  const dimmed = disabled || ariaDisabled;

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={classNames(
        'inline-flex items-center gap-1 py-1 rounded text-base leading-6 font-normal',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2',
        tone === 'blue' && 'text-cs-blue-500 active:text-cs-blue-600',
        tone === 'gray' && 'text-cs-gray-700',
        dimmed ? 'opacity-40 cursor-not-allowed' : 'lg:hover:underline',
        className
      )}
      {...rest}
    >
      {Icon && <Icon className="size-4 flex-shrink-0" aria-hidden="true" />}
      <span>{label}</span>
    </button>
  );
});

TextButton.propTypes = {
  label: PropTypes.node.isRequired,
  icon: PropTypes.elementType,
  tone: PropTypes.oneOf(['blue', 'gray']),
  disabled: PropTypes.bool,
  className: PropTypes.string
};

export default TextButton;
